import { CSSProperties } from "react";

import { scaleBand } from "@visx/scale";
import { compact } from "lodash/fp";

import { Series, Orientation } from "./types";

/**
 *
 * @param series
 * @param round round the values to the nearest integer (default true)
 * @param roundTo round the values to the nearest multiple of roundTo or "auto" (default "auto")
 * @param padding padding in units around the series range (default "auto")
 */
export const getSeriesRange = (
  series: (number | null)[],
  {
    round = true,
    padding = "auto",
    roundTo = "auto",
  }: {
    round?: boolean;
    padding?: number | "auto";
    roundTo?: number | "auto";
  } = {}
): { min: number; max: number; midpoint: number } => {
  const compactedSeries = compact(series);
  const seriesMin = Math.min(...compactedSeries);
  const seriesMax = Math.max(...compactedSeries);

  let min = seriesMin;
  let max = seriesMax;

  let multipleToRoundTo;
  if (roundTo === "auto") {
    // round to the nearest order of magnitude between the range
    multipleToRoundTo = Math.max(
      Math.pow(10, Math.floor(Math.log10(max - min))),
      1
    );
  } else {
    multipleToRoundTo = roundTo;
  }

  let paddingToUse;
  if (padding === "auto") {
    paddingToUse = Math.ceil(multipleToRoundTo / 2) + 2;
  } else {
    paddingToUse = padding;
  }

  min = seriesMin - min >= paddingToUse ? min : seriesMin - paddingToUse;
  max = max - seriesMax >= paddingToUse ? max : max + paddingToUse;

  if (round) {
    min = Math.floor(min / multipleToRoundTo) * multipleToRoundTo;
    max = Math.ceil(max / multipleToRoundTo) * multipleToRoundTo;
  }

  return { min: Math.max(0, min), max, midpoint: (max - min) / 2 + min };
};

type XYReturnType<T extends Series<Orientation>> =
  T extends Series<"vertical">
    ? { x: string; y: number }[]
    : { x: number; y: string }[];

/** Convert a {Series} to an list of x/y coordinates */
export const getXYPointsFromSeries = <T extends Series<Orientation>>(
  lineSeries: T
) => {
  const { x, y } = lineSeries;
  return y.map((yValue, index) => ({
    x: x[index],
    y: yValue,
  })) as XYReturnType<T>;
};

export const scaleBandInvertIndex = (
  scale: ReturnType<typeof scaleBand<string>>,
  value: number
) => {
  const eachBand = scale.step();
  const domain = scale.domain();
  const padding = (scale(domain[0]) ?? 0) / 2;
  return Math.max(
    0,
    Math.min(Math.floor((value - padding) / eachBand), domain.length - 1)
  );
};

export const getFillColor = (
  fillColor:
    | CSSProperties["fill"]
    | ((d: number | null) => CSSProperties["fill"]),
  value: number | null
): CSSProperties["fill"] => {
  if (typeof fillColor === "string" || fillColor === undefined) {
    return fillColor;
  }
  return fillColor(value);
};
